import React from 'react'

import { Dropdown, SFSymbolLabel } from '@sketch/components'
import { useGetSectionsForPageQuery } from '@sketch/gql-types'

import { useVersioning } from '../../../versioning'
import { getSiblingSections } from '../../SectionView/utils/getSiblingSections'

import NavigationItem from './NavigationItem'
import { DropdownItem } from './NavigationItem.styles'
import { Container } from './FramesNavigationItem.styles'

interface SectionsNavigationItemProps {
  shareIdentifier: string
  pageUUID: string
  currentSectionUUID: string
  currentSectionName: string
}

const useSiblingSections = (
  shareIdentifier: string,
  pageUUID: string,
  currentSectionUUID: string
) => {
  const { versionShortId } = useVersioning()

  const { data } = useGetSectionsForPageQuery({
    variables: {
      identifier: shareIdentifier,
      versionShortId,
      pageUUID,
    },
  })

  // Show the current Section's siblings; its own children are reached by
  // navigating into it, not from this dropdown.
  const allSections = data?.share?.version?.document?.page?.sections ?? []
  return getSiblingSections(allSections, currentSectionUUID)
}

const SectionsDropdown = ({
  shareIdentifier,
  pageUUID,
  currentSectionUUID,
}: SectionsNavigationItemProps) => {
  const sections = useSiblingSections(
    shareIdentifier,
    pageUUID,
    currentSectionUUID
  )
  const { getPathname } = useVersioning()

  return (
    <Container>
      <Dropdown.Header>Sections</Dropdown.Header>
      {sections.map(section => (
        <DropdownItem
          key={section.uuid}
          isActive={match => !!match || section.uuid === currentSectionUUID}
          to={getPathname({
            routeKey: 'PAGE_SECTION',
            routeParams: {
              shareID: shareIdentifier,
              pageUUID,
              sectionUUID: section.uuid,
            },
          })}
        >
          <SFSymbolLabel text={section.name ?? ''} />
        </DropdownItem>
      ))}
    </Container>
  )
}

export const SectionsNavigationItem = (props: SectionsNavigationItemProps) => {
  const sections = useSiblingSections(
    props.shareIdentifier,
    props.pageUUID,
    props.currentSectionUUID
  )

  const currentSection = sections.find(
    section => section.uuid === props.currentSectionUUID
  )
  const icon = currentSection?.isVariantSet ? 'variantSet' : 'section'

  if (sections.length <= 1) {
    return <NavigationItem icon={icon} label={props.currentSectionName || ''} />
  }

  return (
    <NavigationItem
      data-testid="section-selector"
      icon={icon}
      label={<SFSymbolLabel text={props.currentSectionName} />}
      dropdownComponent={SectionsDropdown}
      dropdownComponentProps={props}
    />
  )
}
